/**
 * WB4 — Palette operational filter handlers (navigate + query; not kernel mutation).
 *
 * Honest operational commands: route to list surfaces with filter query params.
 * Registered filter:* refs are executable from palette without explain-only fallback.
 */

export interface PaletteOperationalFilterContext {
  object?: { id?: string };
  businessDomain?: string;
}

export interface PaletteOperationalFilterSpec {
  id: string;
  label: string;
  handlerRef: string;
  route: string;
  buildSearch?: (ctx: PaletteOperationalFilterContext) => string;
  keywords: readonly string[];
}

function buildScopedSearch(
  ctx: PaletteOperationalFilterContext,
  base: string,
  scopeKey: string
): string {
  const scopeId = ctx.object?.id?.trim();
  if (!scopeId) {
    return base;
  }
  const params = new URLSearchParams(base.startsWith('?') ? base.slice(1) : base);
  params.set(scopeKey, scopeId);
  return `?${params.toString()}`;
}

function buildCustomerScopedSearch(ctx: PaletteOperationalFilterContext, base: string): string {
  return buildScopedSearch(ctx, base, 'customerId');
}

function buildSupplierScopedSearch(ctx: PaletteOperationalFilterContext, base: string): string {
  return buildScopedSearch(ctx, base, 'supplierId');
}

export const PALETTE_OPERATIONAL_FILTER_SPECS: readonly PaletteOperationalFilterSpec[] = [
  {
    id: 'filter-overdue-ar',
    label: '연체 AR 보기',
    handlerRef: 'filter:overdue-ar',
    route: '/finance/receivables',
    buildSearch: () => '?aging=overdue',
    keywords: ['overdue', 'ar', '연체', '미수'],
  },
  {
    id: 'filter-overdue-receivables',
    label: '연체 수금 목록',
    handlerRef: 'filter:overdue-receivables',
    route: '/finance/receivables',
    buildSearch: () => '?aging=overdue',
    keywords: ['collection', 'receivable', '수금', '연체'],
  },
  {
    id: 'filter-pending-orders',
    label: '대기 주문 보기',
    handlerRef: 'filter:pending-orders',
    route: '/order/production-order',
    buildSearch: () => '?status=pending',
    keywords: ['pending', 'order', '대기', '주문'],
  },
  {
    id: 'filter-pending-tasks',
    label: '대기 작업 보기',
    handlerRef: 'filter:pending-tasks',
    route: '/flow/assignments',
    buildSearch: () => '?status=pending',
    keywords: ['pending', 'task', 'assignment', '대기', '작업', '배정'],
  },
  {
    id: 'filter-customer-overdue-orders',
    label: '이 고객 연체 주문',
    handlerRef: 'filter:customer-overdue-orders',
    route: '/order/production-order',
    buildSearch: (ctx) => buildCustomerScopedSearch(ctx, '?status=overdue'),
    keywords: ['customer', 'overdue', '고객', '연체', '주문'],
  },
  {
    id: 'filter-supplier-inventory-shortages',
    label: '공급 부족 재고',
    handlerRef: 'filter:supplier-inventory-shortages',
    route: '/inventory/shortage',
    buildSearch: (ctx) => buildSupplierScopedSearch(ctx, '?status=shortage'),
    keywords: ['supplier', 'shortage', 'purchase', '공급', '부족', '구매', '재고'],
  },
  {
    id: 'filter-supplier-receiving-queue',
    label: '입고 큐 보기',
    handlerRef: 'filter:supplier-receiving-queue',
    route: '/warehouse/receiving',
    buildSearch: (ctx) => buildSupplierScopedSearch(ctx, '?status=pending'),
    keywords: ['supplier', 'receiving', 'inventory', '입고', '공급', '창고'],
  },
] as const;

const HANDLER_REF_SET = new Set(PALETTE_OPERATIONAL_FILTER_SPECS.map((spec) => spec.handlerRef));

export function isPaletteOperationalFilterHandlerRef(handlerRef: string | undefined): boolean {
  return Boolean(handlerRef && HANDLER_REF_SET.has(handlerRef));
}

export function resolvePaletteOperationalFilterSpec(
  handlerRef: string
): PaletteOperationalFilterSpec | undefined {
  return PALETTE_OPERATIONAL_FILTER_SPECS.find((spec) => spec.handlerRef === handlerRef);
}

export function resolvePaletteOperationalFilterNavigation(
  handlerRef: string,
  ctx: PaletteOperationalFilterContext
): { pathname: string; search: string } | undefined {
  const spec = resolvePaletteOperationalFilterSpec(handlerRef);
  if (!spec) {
    return undefined;
  }
  const search = spec.buildSearch?.(ctx) ?? '';
  return { pathname: spec.route, search };
}
